// UI strings of ShareGate Protect's shared components: dialogs, toasts, pagers, tables and user and workspace labels.
// Their readers are IT admins and the employees who review the Microsoft 365 workspaces they own. Use a plain, neutral tone.
// Translate only the string values. Keep every key and every {{placeholder}} exactly as written.
// "ShareGate", "Microsoft" and "OneDrive" are product names and stay untranslated.
export const en = {
    // Confirmation dialog asking the user to confirm an action, shown when the caller supplies no wording of its own.
    ConfirmationAlert: {
        cancelLabel: "No",
        confirmLabel: "Yes",
        content: "Do you want to continue with this action?",
        // Checkbox the user must tick before confirming a risky action.
        doubleConfirmationText: "I understand the risk",
        title: "Are you sure?"
    },
    // Short notification that pops up at the top of the page.
    Toast: {
        // Screen-reader-only label of the button that closes the notification.
        dismiss: "Dismiss"
    },
    // Screen-reader-only label of the page area that holds the notifications.
    ToastProvider: {
        label: "Notifications"
    },
    // Page navigation below a list or table.
    Pager: {
        // Screen-reader-only label of the page navigation.
        navigationLabel: "pagination",
        // Screen-reader-only label of the "…" button that skips back five pages.
        jumpBack: "Jump back five pages",
        // Screen-reader-only label of the "…" button that skips forward five pages.
        jumpForward: "Jump forward five pages",
        // Screen-reader-only label of the current page's button. {{page}} is the page number.
        currentPage: "Page {{page}}",
        // Screen-reader-only label of another page's button. {{page}} is the page number.
        goToPage: "go to page {{page}}",
        // Screen-reader-only label of the previous-page arrow button.
        previousPage: "Go to previous page",
        // Screen-reader-only label of the next-page arrow button.
        nextPage: "Go to next page",
        // Items shown on the current page. {{start}} and {{end}} are its first and last item numbers, {{total}} the item count.
        range: "{{start}}-{{end}} of {{total}}"
    },
    // Grey placeholder shown while content loads.
    Skeleton: {
        // Screen-reader-only label of the placeholder.
        label: "Data Loading"
    },
    // Error message shown in place of a table that failed to load.
    TableErrored: {
        refresh: "Refresh page"
    },
    // Icon of a Microsoft 365 workspace, with a tooltip naming its type.
    WorkspaceIcon: {
        group: "Group",
        team: "Team",
        // Product name: keep as written.
        oneDrive: "OneDrive",
        // A SharePoint site.
        sharePointSite: "Site",
        teamsChannel: "Team channel",
        // Screen-reader-only label of the icon of a workspace whose type is unknown.
        unknown: "Unknown",
        unknownTooltip: "Workspace type is unknown"
    },
    // A user's avatar and name, with a tag for owners.
    UserIdentity: {
        // Screen-reader-only label of an avatar that opens the user's details.
        viewUserDetails: "View user details",
        // Screen-reader-only label of an avatar.
        user: "User",
        // Tag next to a user who is also an owner of the workspace. Keep it very short.
        owner: "+ Owner"
    },
    // Tag next to a user's name marking a user from outside the organization.
    UserTypeTag: {
        // A guest account invited into the organization.
        guest: "Guest",
        // A user from another organization.
        external: "External"
    },
    // Table of users with an action button on each row.
    ManageUsersTable: {
        // Screen-reader-only header of the column holding each row's action button.
        rowActions: "Row actions"
    },
    // Dialog listing the people a sharing link grants access to, where the user picks recipients to remove.
    SharingLinkRecipientsModalContent: {
        loadError: "Could not load the recipients of this link. Try again later.",
        // Warning shown when every recipient is picked for removal, keyed by how the link would be removed instead.
        emptyLinkWarning: {
            delete: "The link will stay, but it won't grant access to anyone. To remove it completely, delete the link instead.",
            revoke: "The link will stay, but it won't grant access to anyone. To remove it completely, delete the link instead."
        },
        // Screen-reader-only label of the loading spinner.
        loading: "Loading…",
        cancel: "Cancel",
        apply: "Apply",
        // Row button that cancels a recipient's pending removal.
        undo: "Undo",
        // Row button that picks a recipient for removal.
        remove: "Remove",
        noRecipients: "This link has no recipients.",
        noMatch: "No recipients match your search.",
        // Screen-reader-only label of the search field.
        searchLabel: "Search recipients",
        searchPlaceholder: "Search by name or email…",
        // Screen-reader-only label of the recipients table.
        tableLabel: "Recipients",
        // Screen-reader-only label of the table's only column.
        columnLabel: "Recipients",
        loadMore: "Load more recipients",
        // Note below the list. {{count}} is the number of recipients picked for removal that the list doesn't show.
        hiddenSelected_one: "{{count}} more selected recipient isn't shown.",
        hiddenSelected_other: "{{count}} more selected recipients aren't shown.",
        // Header of the recipients column. {{formattedCount}} is the total number of recipients.
        recipientCount_one: "{{formattedCount}} recipient",
        recipientCount_other: "{{formattedCount}} recipients"
    },
    // Kind of a sharing link recipient, shown under or next to the recipient's name.
    SharingLinkRecipientKind: {
        // A user who belongs to the organization.
        member: "Member",
        group: "Group",
        unknown: "Unknown"
    },
    // A duration placed inside a sentence, as in "Inactive for 4 months" or "3 days left". {{count}} is the number of units.
    DaysCount: {
        day_one: "{{count}} day",
        day_other: "{{count}} days",
        month_one: "{{count}} month",
        month_other: "{{count}} months",
        year_one: "{{count}} year",
        year_other: "{{count}} years"
    },
    // A past moment, as in a "Saved just now" indicator. {{time}} is a clock time, {{date}} a short month and day.
    RelativeTime: {
        justNow: "just now",
        today: "Today at {{time}}",
        yesterday: "Yesterday at {{time}}",
        otherDay: "{{date}} at {{time}}"
    },
    // Keycap of a keyboard shortcut, as printed on a Windows or Linux keyboard. Keep each very short.
    Shortcut: {
        mod: "Ctrl",
        shift: "Shift",
        alt: "Alt",
        escape: "Esc"
    },
    // Side panel that slides in from the right of the page.
    SidePanel: {
        // Screen-reader-only label of the button that closes the panel.
        dismiss: "Dismiss"
    },
    // Dialog shown when the user leaves a page with unsaved changes.
    PreventNavigationModal: {
        heading: "Discard your changes?",
        content: "If you leave, any changes you made will be lost.",
        // Buttons when the page can save the changes before leaving.
        confirm: "Confirm",
        discard: "Discard",
        // Buttons when the changes can only be thrown away.
        discardChanges: "Discard changes",
        keepEditing: "Keep editing"
    },
    // Button that downloads a report as a file.
    ExportButton: {
        label: "Export",
        // Notification shown when the download starts.
        success: "Generating your report, please stay on the page.",
        error: "We couldn't generate your report. Please try again"
    },
    // Spinner shown in the export button while the file is generated.
    ExportIndicator: {
        label: "Exporting"
    },
    // Card showing one number, such as a count of users or files.
    MetricCard: {
        // Screen-reader-only label of a card whose number is loading.
        loading: "Loading",
        // Screen-reader-only label of a card whose number failed to load.
        error: "Error",
        unavailable: "Data is unavailable"
    },
    // Line chart of a value over time.
    LineChart: {
        notEnoughData: "Not enough data available to display the chart."
    },
    // Tooltip of a point on a line chart. {{value}} is the point's number.
    LineChartTooltip: {
        value: "Value: {{value}}"
    },
    // Pie chart.
    PieChart: {
        // Name of the single grey slice drawn when there is no data.
        noData: "No data"
    },
    // Tooltip of a pie chart slice. {{name}} is the slice's name, {{value}} its number.
    PieChartTooltip: {
        label: "{{name}}: {{value}}"
    },
    // Tooltip of a storage pie chart slice. {{percentage}} is its share of the total storage.
    StorageChart: {
        tooltip: "{{percentage}} total storage"
    },
    // Tooltip of a segment of a horizontal bar chart. {{percentage}} is its share of the total, {{formattedCount}} its number, {{category}} its name.
    CumulativeBarChart: {
        tooltip: "{{percentage}} ({{formattedCount}}) {{category}}"
    },
    // List of horizontal bars, one per category.
    DistributionBarChart: {
        // Shown in place of a bar the user has no permission to see.
        notPermitted: "Not permitted"
    },
    // Heading above a table. {{count}} is a number of rows, {{label}} the name of what the rows are, already singular or plural.
    TableSummaryCountHeading: {
        count: "{{count}} {{label}}",
        // Number of rows before filtering, shown next to the heading.
        total: "{{count}} total"
    },
    // Tenant name with an info popover about the Microsoft 365 tenant.
    TenantInformation: {
        // Screen-reader-only label of the placeholder shown while the name loads.
        loadingName: "Loading Tenant Name",
        // {{tenantId}} is a long identifier.
        tenantId: "Tenant ID: {{tenantId}}",
        // {{region}} is a data center region, such as "Canada".
        dataRegion: "Assessment data stored in {{region}}"
    },
    // Name of a workspace in a table, with a link that opens it in Microsoft 365.
    WorkspaceNameCell: {
        // Screen-reader-only label of the link icon.
        openWorkspace: "URL to workspace",
        // Shown in place of the name of a workspace that has none.
        unnamed: {
            site: "Unnamed site",
            workspace: "Unnamed workspace"
        }
    },
    // ShareGate logo in the page header.
    ShareGateWordmarkLogo: {
        // Screen-reader-only label of the logo.
        label: "ShareGate Logo"
    },
    // Card of an assessment report section.
    AssessmentLayer: {
        showOverview: "Show overview"
    },
    // Box explaining a risk in an assessment report section.
    PotentialRisksCard: {
        title: "Potential Risk"
    },
    // Boxes of an assessment report section: a percentage, why it matters, and suggestions.
    Suggestions: {
        whyItMatters: "Why it matters"
    },
    // Box listing suggested actions in an assessment report section. {{count}} is the number of suggestions.
    SuggestionsCard: {
        title_one: "{{count}} Suggestions",
        title_other: "{{count}} Suggestions"
    },
    // Card listing highlights of an assessment, each with a button to the matching report.
    KeyPoints: {
        title: "Key points we’ve highlighted for you",
        // Button opening a report, keyed by what the report lists.
        review: {
            users: "Review users",
            exchange: "Review mailboxes",
            onedrive: "Review accounts",
            teams: "Review teams",
            site: "Review sites",
            googleDrive: "Review drives",
            gmail: "Review mailboxes"
        }
    }
};
